"use client";

import { FormElement, MsDistrictFilterBlock } from "@/payload-types";
import { AnyFieldApi } from "@tanstack/react-form-nextjs";
import { useEffect, useMemo, useState } from "react";
import { Checkbox, CheckboxGroup } from "@/components/frontend/ui/form/inputs/checkbox";
import Combobox, { ComboboxOption } from "@/components/frontend/ui/form/inputs/combobox";
import Description from "@/components/frontend/ui/form/inputs/description";
import { Toggle, ToggleGroup } from "@/components/frontend/ui/toggle-group";
import type { FilterOption, FilterValue, SelectedFilters } from "@/lib/types/marketsuite/datatypes/filters";
import { Relationship } from "@/lib/utils/theme-configuration-class";
import { capitalizeWords } from "@/lib/utils/string";
import { SelectFilterDefinition } from "../schemas/select";
import Label from "@/components/frontend/ui/form/inputs/label";
import { Search, SearchX } from "lucide-react";
import Input from "@/components/frontend/ui/form/inputs/input";
import { useTranslations } from "next-intl";

// TODO: This type is ugly, make more robust
export type OptionsSelectFilterBlock = Omit<MsDistrictFilterBlock, "blockType" | "id" | "blockName">;
export interface MsSelectFilterProps extends OptionsSelectFilterBlock {
	blockType: SelectFilterDefinition["slug"] | "listing-type";
	field: AnyFieldApi;
	selectedFilters: SelectedFilters;
	filterData?: FilterOption[] | string[];
	onValueChange?: (value: FilterValue, blockType: SelectFilterDefinition["slug"] | "listing-type") => void;
	formVariant?: Relationship<FormElement>;
}

type Translator = ReturnType<typeof useTranslations>;

/** Resolves filter option labels from property-type or characteristic translations. */
function optionLabel(t: Translator, code: string, label: string) {
	const propertyTypeKey = `property-types.${code}`;
	if (t.has(propertyTypeKey)) return t(propertyTypeKey);

	const key = `characteristics.values.${code.toLowerCase()}`;

	return t.has(key) ? t(key) : capitalizeWords(label);
}

function toComboboxOptions(options: FilterOption[], selected: string[], t: Translator): ComboboxOption[] {
	const comboboxOptions = options.map((option) => ({
		value: option.code,
		label: option.label,
	}));
	const known = new Set(comboboxOptions.map((option) => option.value));

	for (const code of selected) {
		if (known.has(code)) continue;
		comboboxOptions.push({ value: code, label: optionLabel(t, code, code) });
		known.add(code);
	}

	return comboboxOptions;
}

export const MsSelectFilter: React.FC<MsSelectFilterProps> = ({
	blockType,
	field,
	selectedFilters,
	onValueChange,
	content,
	settings,
	appearance,
	options: staticOptions,
	filterData,
	formVariant,
}) => {
	const t = useTranslations("ms");
	const multiple = settings?.multiple ?? false;
	const selected = useMemo(() => {
		const blockFilters = selectedFilters[blockType];

		if (!Array.isArray(blockFilters)) return [];

		return blockFilters.filter((item) => typeof item === "string");
	}, [selectedFilters]);

	const options = useMemo(() => {
		let rawOptions = filterData;

		if (settings?.dynamic === false) {
			rawOptions = (staticOptions?.values ?? [])
				.map((entry) => entry.value)
				.filter((value): value is string => value != null)
				.map((value) => ({ code: value, label: value }));
		}

		return rawOptions?.map((option) => {
			const { code, label } = typeof option === "string" ? { code: option, label: option } : option;

			return { code, label: optionLabel(t, code, label) };
		});
	}, [settings?.dynamic, staticOptions, filterData, t]);

	const handleChange = (value: string[]) => {
		field.handleChange(value);
		onValueChange?.(value, blockType);
	};

	useEffect(() => {
		if (appearance?.inputType === "toggleGroup" && selected.length === 0 && options && options.length > 0) {
			const firstCode = options[0]?.code;
			if (firstCode) handleChange([firstCode]);
		}
	}, [appearance?.inputType, options, selected.length]);

	if (!options) return null;

	if (appearance?.inputType === "select") {
		const comboboxOptions = toComboboxOptions(options, selected, t);
		const selectedOptions = selected.map(
			(code) => comboboxOptions.find((option) => option.value === code) ?? { value: code, label: optionLabel(t, code, code) },
		);

		return (
			// @ts-expect-error - Multiple is a literal boolean
			<Combobox
				multiple={multiple}
				options={comboboxOptions}
				id={field.name}
				name={field.name}
				value={multiple ? selectedOptions : selectedOptions[0]}
				formVariant={formVariant}
				onBlur={field.handleBlur}
				label={content?.label ?? undefined}
				description={content?.description ?? undefined}
				placeholder={content?.placeholder ?? undefined}
				search={settings?.searchable ?? true}
				onValueChange={(v: ComboboxOption | ComboboxOption[]) => {
					const value = Array.isArray(v) ? v.map((item) => item.value) : [v.value];
					handleChange(value);
				}}
			/>
		);
	}

	if (appearance?.inputType === "toggleGroup") {
		return (
			<div className="flex flex-col gap-1">
				<ToggleGroup
					variant={appearance.toggleGroupVariant}
					className="w-full"
					multiple={multiple}
					value={selected}
					onValueChange={(value) => {
						if (!multiple && value.length === 0) return;
						handleChange(multiple ? value : value.slice(-1));
					}}
				>
					{options.map(
						(option) =>
							option.code && (
								<Toggle key={option.code} value={option.code}>
									{option.label}
								</Toggle>
							),
					)}
				</ToggleGroup>

				{content?.description && <Description>{content.description}</Description>}
			</div>
		);
	}

	return <Checkboxes content={content} selected={selected} handleChange={handleChange} field={field} options={options} multiple={multiple} />;
};

interface CheckboxesProps extends Pick<MsSelectFilterProps, "content" | "field"> {
	selected: string[];
	options: FilterOption[];
	multiple: boolean;
	handleChange: (val: string[]) => void;
}

const Checkboxes: React.FC<CheckboxesProps> = ({ content, selected, options, multiple, handleChange, field }) => {
	const t = useTranslations("ms");
	const [searchEnabled, setSearchEnabled] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");

	const filteredOptions = useMemo(() => {
		const query = searchQuery.trim().toLowerCase();
		if (!query) return options;

		return options.filter(
			(option) => (!multiple && selected.includes(option.code)) || (!selected.includes(option.code) && option.label.toLowerCase().includes(query)),
		);
	}, [options, selected, searchQuery, multiple]);

	const toggleSearch = () => {
		setSearchEnabled((enabled) => {
			if (enabled) setSearchQuery("");
			return !enabled;
		});
	};

	return (
		<CheckboxGroup value={selected} id={field.name} onValueChange={handleChange}>
			<Label variant="legend">
				<div className="flex items-center gap-2">
					<span>{content?.label ?? ""}</span>

					{options.length > 10 && (
						<button onClick={toggleSearch} className="cursor-pointer transition hover:scale-110">
							{searchEnabled ? <SearchX size={14} /> : <Search size={14} />}
						</button>
					)}
				</div>
			</Label>

			{content?.description && <Description>{content.description}</Description>}

			{multiple && (
				<div className="border-b border-(--ms-card-border-color) pb-2 empty:hidden">
					{options.map((option) => selected.includes(option.code) && <Checkbox key={option.code} label={option.label} value={option.code} />)}
				</div>
			)}

			<div className="max-h-40 overflow-y-auto pt-2 [&>label:has([data-disabled])]:opacity-60">
				{searchEnabled && (
					<Input
						autoFocus
						onBlur={() => !searchQuery && setSearchEnabled(false)}
						placeholder={t("filters.search-placeholder")}
						value={searchQuery}
						onChange={(event) => setSearchQuery(event.target.value)}
						containerClassName="mb-2"
					/>
				)}

				{filteredOptions.length === 0 && <span>{t("filters.no-options-found")}</span>}

				{filteredOptions.map(
					(option) =>
						(!multiple || !selected.includes(option.code)) && (
							<Checkbox
								key={option.code}
								label={option.label}
								value={option.code}
								disabled={!multiple && selected.length > 0 && !selected.includes(option.code)}
							/>
						),
				)}
			</div>
		</CheckboxGroup>
	);
};
